import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminApiInstance, adminGet, adminPost, adminPatch, BACKEND } from "../../lib/adminApi";
import { PageHeader, ErrorBanner, OnbruikbaarMerkje, OvergeslagenBanner, StatusBadge, formatDate, LoadingRow, EmptyRow } from "../../components/admin/AdminUI";
import { NotitieVeld } from "../../components/admin/NotitieVeld";
import QRCode from "qrcode";

const TABS = ["profiel", "aanmeldlink", "werknemers", "rapport", "notities", "csv-import"];

export default function WerkgeverDetailPage() {
  const { id } = useParams();
  const [tab, setTab] = useState("profiel");

  const q = useQuery({
    queryKey: ["werkgever", id],
    queryFn: () => adminGet(`/api/admin/werkgevers/${id}`),
  });

  return (
    <div data-testid="werkgever-detail-page">
      <PageHeader
        title={q.data?.bedrijfsnaam || "Werkgever"}
        subtitle={q.data?.aanmeldcode ? `Aanmeldcode ${q.data.aanmeldcode}` : undefined}
        actions={q.data ? <WerkgeverStatusActies werkgever={q.data} /> : null}
      />
      <ErrorBanner error={q.error} />
      <div className="mb-4 flex gap-2 border-b border-slate-200" role="tablist">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            onClick={() => setTab(t)}
            data-testid={`tab-${t}`}
            className={`px-3 py-2 text-sm font-medium ${tab === t ? "border-b-2 border-blue-600 text-blue-700" : "text-slate-600 hover:text-slate-900"}`}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === "profiel" && <ProfielTab w={q.data} loading={q.isLoading} />}
      {tab === "aanmeldlink" && <AanmeldlinkTab w={q.data} />}
      {tab === "werknemers" && <WerknemersTab werkgeverId={id} deal={q.data?.aantal_werknemers_deal} />}
      {tab === "rapport" && <RapportTab werkgeverId={id} />}
      {tab === "notities" && <NotitieVeld entiteitType="werkgever" entiteitId={id} />}
      {tab === "csv-import" && <CsvImportTab werkgeverId={id} />}
    </div>
  );
}

function WerkgeverStatusActies({ werkgever }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (body) => adminPatch(`/api/admin/werkgevers/${werkgever.werkgever_id}`, body),
    onSuccess: () => { toast.success("Status bijgewerkt"); qc.invalidateQueries({ queryKey: ["werkgever", werkgever.werkgever_id] }); },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Status-update mislukt"),
  });
  const huidigeStatus = werkgever.status;
  function setStatus(nieuw) {
    if (!confirm(`Werkgever-status wijzigen van '${huidigeStatus}' naar '${nieuw}'?`)) return;
    mut.mutate({ status: nieuw });
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-slate-500">Status:</span>
      <StatusBadge value={huidigeStatus} />
      {huidigeStatus === "actief" ? (
        <>
          <button type="button" onClick={() => setStatus("gepauzeerd")} disabled={mut.isPending}
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-60">Pauzeren</button>
          <button type="button" onClick={() => setStatus("beeindigd")} disabled={mut.isPending}
            className="rounded-md border border-rose-300 bg-rose-50 px-3 py-1 text-xs font-medium text-rose-800 hover:bg-rose-100 disabled:opacity-60">Beëindigen</button>
        </>
      ) : huidigeStatus === "gepauzeerd" ? (
        <>
          <button type="button" onClick={() => setStatus("actief")} disabled={mut.isPending}
            className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-60">Hervatten</button>
          <button type="button" onClick={() => setStatus("beeindigd")} disabled={mut.isPending}
            className="rounded-md border border-rose-300 bg-rose-50 px-3 py-1 text-xs font-medium text-rose-800 hover:bg-rose-100 disabled:opacity-60">Beëindigen</button>
        </>
      ) : (
        <button type="button" onClick={() => setStatus("actief")} disabled={mut.isPending}
          className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">Reactiveren</button>
      )}
    </div>
  );
}

function ProfielTab({ w, loading }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  if (loading) return <div className="text-sm text-slate-500" data-testid="profiel-loading">Laden…</div>;
  if (!w) return null;

  return editing ? (
    <ProfielEditForm w={w} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); qc.invalidateQueries({ queryKey: ["werkgever", w.werkgever_id] }); }} />
  ) : (
    <div className="space-y-4" data-testid="profiel-tab">
      <section className="rounded-md border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">Bedrijf</h2>
          <button type="button" onClick={() => setEditing(true)} data-testid="btn-profiel-edit"
            className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50">Bewerken</button>
        </div>
        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div><dt className="text-xs uppercase tracking-wide text-slate-500">Bedrijfsnaam</dt><dd>{w.bedrijfsnaam}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-slate-500">Status</dt><dd><StatusBadge value={w.status} /></dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-slate-500">KVK-nummer</dt><dd data-testid="detail-kvk">{w.kvk_nummer || "—"}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-slate-500">Sector</dt><dd>{w.sector || "—"}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-slate-500">Aantal werknemers (deal)</dt><dd>{w.aantal_werknemers_deal ?? "—"}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-slate-500">Adres</dt><dd>{w.adres || "—"}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-slate-500">Postcode / Plaats</dt><dd>{[w.postcode, w.plaats].filter(Boolean).join("  ") || "—"}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-slate-500">Standaard aanmeet-locatie</dt><dd>{w.standaard_aanmeet_locatie || "—"}</dd></div>
          <div><dt className="text-xs uppercase tracking-wide text-slate-500">Aangemaakt</dt><dd>{formatDate(w.created_at)}</dd></div>
        </dl>
      </section>

      <section className="rounded-md border border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Contactpersoon</h2>
        {w.contactpersoon_naam ? (
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <div><dt className="text-xs uppercase tracking-wide text-slate-500">Naam</dt><dd>{w.contactpersoon_naam}</dd></div>
            <div><dt className="text-xs uppercase tracking-wide text-slate-500">Rol</dt><dd>{w.contactpersoon_rol || "—"}</dd></div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">E-mail</dt>
              <dd>
                {w.contactpersoon_email ? <a href={`mailto:${w.contactpersoon_email}`} className="text-blue-600 hover:underline">{w.contactpersoon_email}</a> : "—"}
                {/* Dit staat hier als klikbare mailto-link; een adres waar geen mail
                    heen kan hoort daarom gemerkt te worden. */}
                {w.contactpersoon_email && w.contactpersoon_email_geldig === false ? (
                  <OnbruikbaarMerkje testid="contactpersoon-email-onbruikbaar" />
                ) : null}
              </dd>
            </div>
            <div><dt className="text-xs uppercase tracking-wide text-slate-500">Telefoon</dt><dd>{w.contactpersoon_telefoon ? <a href={`tel:${w.contactpersoon_telefoon}`} className="text-blue-600 hover:underline">{w.contactpersoon_telefoon}</a> : "—"}</dd></div>
          </dl>
        ) : (
          <p className="text-sm text-slate-500">Nog geen contactpersoon ingevuld. Klik op <span className="font-medium">Bewerken</span> om toe te voegen.</p>
        )}
      </section>
    </div>
  );
}

function ProfielEditForm({ w, onCancel, onSaved }) {
  const [form, setForm] = useState({
    bedrijfsnaam: w.bedrijfsnaam || "",
    sector: w.sector || "",
    adres: w.adres || "",
    postcode: w.postcode || "",
    plaats: w.plaats || "",
    standaard_aanmeet_locatie: w.standaard_aanmeet_locatie || "",
    aantal_werknemers_deal: w.aantal_werknemers_deal ?? "",
    kvk_nummer: w.kvk_nummer || "",
    contactpersoon_naam: w.contactpersoon_naam || "",
    contactpersoon_rol: w.contactpersoon_rol || "",
    contactpersoon_email: w.contactpersoon_email || "",
    contactpersoon_telefoon: w.contactpersoon_telefoon || "",
  });
  const mut = useMutation({
    mutationFn: (body) => adminPatch(`/api/admin/werkgevers/${w.werkgever_id}`, body),
    onSuccess: () => { toast.success("Werkgever bijgewerkt"); onSaved(); },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Bijwerken mislukt"),
  });
  const [getoond, setGetoond] = useState(false);
  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  // De bedrijfsnaam is het enige veld dat niet leeg mag: de database eist hem, en een
  // werkgever zonder naam is in elke lijst een lege regel. Vandaar de grens hier, zodat de
  // beheerder een gewone melding ziet in plaats van een databasefout.
  const naam = form.bedrijfsnaam.trim();
  const naamFout = naam.length < 2 ? "Vul een bedrijfsnaam in van minstens twee tekens." : null;

  function submit(e) {
    e.preventDefault();
    setGetoond(true);
    if (naamFout) return;
    const body = { ...form };
    // Leeg betekent "wissen" — behalve bij de bedrijfsnaam, die hierboven al is afgedwongen.
    Object.keys(body).forEach((k) => { if (body[k] === "" && k !== "bedrijfsnaam") body[k] = null; });
    body.bedrijfsnaam = naam;
    if (body.aantal_werknemers_deal != null) body.aantal_werknemers_deal = parseInt(body.aantal_werknemers_deal, 10) || null;
    mut.mutate(body);
  }
  const fieldCls = "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200";
  const labelCls = "block text-sm font-medium text-slate-700";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="profiel-edit-form">
      <section className="rounded-md border border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Bedrijf bewerken</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="sm:col-span-2">
            <span className={labelCls}>Bedrijfsnaam</span>
            <input
              className={fieldCls}
              value={form.bedrijfsnaam}
              onChange={(e) => set("bedrijfsnaam", e.target.value)}
              aria-invalid={getoond && naamFout ? true : undefined}
              data-testid="input-bedrijfsnaam"
            />
            {getoond && naamFout ? (
              <span className="mt-1 block text-xs text-rose-700" data-testid="fout-bedrijfsnaam">{naamFout}</span>
            ) : null}
          </label>
          <label><span className={labelCls}>KVK-nummer</span><input className={fieldCls} value={form.kvk_nummer} onChange={(e) => set("kvk_nummer", e.target.value)} placeholder="12345678" inputMode="numeric" data-testid="input-kvk" /></label>
          <label><span className={labelCls}>Sector</span><input className={fieldCls} value={form.sector} onChange={(e) => set("sector", e.target.value)} data-testid="input-sector" /></label>
          <label><span className={labelCls}>Aantal werknemers (deal)</span><input type="number" min="0" className={fieldCls} value={form.aantal_werknemers_deal} onChange={(e) => set("aantal_werknemers_deal", e.target.value)} data-testid="input-deal-aantal" /></label>
          <label><span className={labelCls}>Adres</span><input className={fieldCls} value={form.adres} onChange={(e) => set("adres", e.target.value)} data-testid="input-adres" /></label>
          <label><span className={labelCls}>Postcode</span><input className={fieldCls} value={form.postcode} onChange={(e) => set("postcode", e.target.value)} placeholder="1234 AB" data-testid="input-postcode" /></label>
          <label className="sm:col-span-2"><span className={labelCls}>Plaats</span><input className={fieldCls} value={form.plaats} onChange={(e) => set("plaats", e.target.value)} data-testid="input-plaats" /></label>
          <label className="sm:col-span-2"><span className={labelCls}>Standaard aanmeet-locatie</span><input className={fieldCls} value={form.standaard_aanmeet_locatie} onChange={(e) => set("standaard_aanmeet_locatie", e.target.value)} data-testid="input-aanmeet-locatie" /></label>
        </div>
      </section>

      <section className="rounded-md border border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Contactpersoon</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label><span className={labelCls}>Naam</span><input className={fieldCls} value={form.contactpersoon_naam} onChange={(e) => set("contactpersoon_naam", e.target.value)} data-testid="input-cp-naam" /></label>
          <label><span className={labelCls}>Rol</span><input className={fieldCls} value={form.contactpersoon_rol} onChange={(e) => set("contactpersoon_rol", e.target.value)} placeholder="HR manager, eigenaar, ..." data-testid="input-cp-rol" /></label>
          <label><span className={labelCls}>E-mail</span><input type="email" className={fieldCls} value={form.contactpersoon_email} onChange={(e) => set("contactpersoon_email", e.target.value)} data-testid="input-cp-email" /></label>
          <label><span className={labelCls}>Telefoon</span><input className={fieldCls} value={form.contactpersoon_telefoon} onChange={(e) => set("contactpersoon_telefoon", e.target.value)} data-testid="input-cp-tel" /></label>
        </div>
      </section>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} data-testid="btn-profiel-cancel"
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">Annuleren</button>
        <button type="submit" disabled={mut.isPending} data-testid="btn-profiel-save"
          className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">{mut.isPending ? "Opslaan…" : "Opslaan"}</button>
      </div>
    </form>
  );
}

function AanmeldlinkTab({ w }) {
  const qc = useQueryClient();
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const url = w?.aanmeldcode ? `${window.location.origin}/aanmeld/${w.aanmeldcode}` : "";

  // Het endpoint POST /aanmeldcode ondersteunt géén regeneratie: als er al een code is
  // geeft de backend 409 (aanmeldcode_bestaat_al). Daarom tonen we de knop alleen als
  // er nog geen code is — een "opnieuw genereren"-knop zou altijd falen en verwarren.
  const mut = useMutation({
    mutationFn: () => adminPost(`/api/admin/werkgevers/${w.werkgever_id}/aanmeldcode`),
    onSuccess: () => {
      toast.success("Aanmeldlink aangemaakt");
      qc.invalidateQueries({ queryKey: ["werkgever", w.werkgever_id] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Aanmeldlink aanmaken mislukt"),
  });

  useEffect(() => {
    if (!url || !canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, url, { width: 256, margin: 1 }).catch(() => {});
  }, [url]);

  function downloadPng() {
    if (!canvasRef.current) return;
    const link = document.createElement("a");
    link.download = `fulkra-works-aanmeld-${w.aanmeldcode}.png`;
    link.href = canvasRef.current.toDataURL("image/png");
    link.click();
  }

  function copyUrl() {
    navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  if (!w) return null;

  if (!w.aanmeldcode) {
    return (
      <div className="rounded-md border border-slate-200 bg-white p-4" data-testid="aanmeldlink-empty">
        <p className="text-sm text-slate-700">
          Deze werkgever heeft nog geen aanmeldlink. Maak er een aan zodat werknemers zich zelf kunnen aanmelden.
        </p>
        <button
          type="button"
          onClick={() => mut.mutate()}
          disabled={mut.isPending}
          data-testid="btn-aanmeldlink-aanmaken"
          className="mt-3 rounded-md bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {mut.isPending ? "Aanmaken…" : "Aanmeldlink aanmaken"}
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4" data-testid="aanmeldlink-tab">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <canvas ref={canvasRef} aria-label="QR-code" data-testid="qr-canvas" className="rounded border border-slate-200" />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wide text-slate-500">Aanmeld-URL</div>
          <code className="mt-1 block break-all rounded bg-slate-100 px-2 py-1 text-xs" data-testid="aanmeld-url">{url}</code>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={copyUrl} data-testid="btn-copy-url"
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
              {copied ? "Gekopieerd ✓" : "Kopieer URL"}
            </button>
            <button type="button" onClick={downloadPng} data-testid="btn-download-qr"
              className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700">
              Download QR (PNG)
            </button>
            <button type="button" disabled data-testid="btn-download-pdf"
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-400" title="Komt in v3B.1">
              Download poster (PDF)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AfvinkBlok({ werkgeverId, deal }) {
  const kpiQ = useQuery({
    queryKey: ["werkgever-kpi", werkgeverId],
    queryFn: () => adminGet(`/api/admin/werkgevers/${werkgeverId}/kpi`),
    enabled: !!werkgeverId,
  });
  const kpi = kpiQ.data || {};
  const st = kpi.status_telling || {};
  // status_telling telt werknemers ná de aanmeldfase óók onder hun huidige status
  // (gepland/aangemeten/...). Voor "ingevuld" = iedereen die de baseline heeft gedaan,
  // dus baseline_voltooid + alle latere statussen. "aangemeld" = wel aangemeld, nog niet ingevuld.
  const NA_BASELINE = ["baseline_voltooid", "gepland", "aangemeten", "verzonden", "geleverd", "week4_voltooid", "week12_voltooid", "afgerond"];
  const ingevuld = NA_BASELINE.reduce((s, k) => s + (st[k] || 0), 0);
  const aangemeld = st.aangemeld || 0;
  const streef = deal ?? kpi.aantal_werknemers ?? 0;
  const pct = streef > 0 ? Math.min(100, Math.round((100 * ingevuld) / streef)) : 0;

  return (
    <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm" data-testid="afvink-blok">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">Aanmeld-voortgang</h2>
        <span className="text-xs text-slate-500" data-testid="afvink-samenvatting">
          {ingevuld} van {streef || "—"} ingevuld
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} data-testid="afvink-progress" />
      </div>
      <dl className="mt-3 grid grid-cols-3 gap-3 text-center">
        <div className="rounded-md border border-slate-200 p-2">
          <dd className="text-xl font-semibold text-emerald-700" data-testid="afvink-ingevuld">{ingevuld}</dd>
          <dt className="text-xs text-slate-500">Ingevuld</dt>
        </div>
        <div className="rounded-md border border-slate-200 p-2">
          <dd className="text-xl font-semibold text-amber-700" data-testid="afvink-aangemeld">{aangemeld}</dd>
          <dt className="text-xs text-slate-500">Aangemeld, nog niet ingevuld</dt>
        </div>
        <div className="rounded-md border border-slate-200 p-2">
          <dd className="text-xl font-semibold text-slate-900" data-testid="afvink-streef">{streef || "—"}</dd>
          <dt className="text-xs text-slate-500">Streefgetal</dt>
        </div>
      </dl>
      <p className="mt-2 text-xs text-slate-500">
        Wie nog niet heeft ingevuld herkent u hieronder aan de status <span className="font-medium">Aangemeld</span>.
      </p>
    </section>
  );
}

function WerknemersTab({ werkgeverId, deal }) {
  const q = useQuery({
    queryKey: ["werkgever-werknemers", werkgeverId],
    queryFn: () => adminGet("/api/admin/werknemers", { werkgever_id: werkgeverId, limit: 100 }),
  });
  const items = q.data?.items || q.data || [];
  return (
    <div className="space-y-4" data-testid="werkgever-werknemers-tab">
    <AfvinkBlok werkgeverId={werkgeverId} deal={deal} />
    {/* Dit is dezelfde lijst en hetzelfde gegevenstype als het oorspronkelijke incident
        (2026-08-19). De hoofdlijst waarschuwt; dit tabblad hoort dat ook te doen. */}
    <OvergeslagenBanner
      aantal={q.data?.overgeslagen} enkelvoud="werknemer" meervoud="werknemers"
      testid="werkgever-werknemers-overgeslagen"
    />
    <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="px-4 py-2 text-left">Klantnr.</th>
            <th className="px-4 py-2 text-left">Naam</th>
            <th className="px-4 py-2 text-left">Status</th>
            <th className="px-4 py-2 text-right">Actie</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {q.isLoading ? <LoadingRow cols={4} /> : items.length === 0 ? <EmptyRow cols={4} /> :
            items.map((w) => (
              <tr key={w.werknemer_id} className="hover:bg-slate-50">
                <td className="px-4 py-2 font-mono text-xs">{w.klantnummer}</td>
                <td className="px-4 py-2">{[w.voorletters, w.tussenvoegsel, w.achternaam].filter(Boolean).join(" ")}</td>
                <td className="px-4 py-2"><StatusBadge value={w.status} /></td>
                <td className="px-4 py-2 text-right">
                  <Link to={`/admin/werknemers/${w.werknemer_id}`} className="text-xs font-medium text-blue-600 hover:underline">Bekijken →</Link>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}

function CsvImportTab({ werkgeverId }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function upload() {
    if (!file) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const api = adminApiInstance();
      const res = await api.post(`/api/admin/werkgevers/${werkgeverId}/werknemers/csv`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4" data-testid="csv-import-tab">
      <p className="text-sm text-slate-700">Upload een CSV met werknemers. Verplichte kolommen: <code>voorletters,achternaam,email,geboortedatum,straat,huisnummer,postcode,plaats</code>.</p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="csv-import-file" />
        <button type="button" onClick={upload} disabled={!file || busy} data-testid="csv-import-submit"
          className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
          {busy ? "Uploaden…" : "Upload CSV"}
        </button>
      </div>
      <ErrorBanner error={error} />
      {result ? (
        <div className="mt-4 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="csv-import-result">
          <div className="font-medium text-emerald-900">Resultaat</div>
          <ul className="mt-2 list-disc pl-5 text-emerald-800">
            <li>Aangeleverd: {result.aantal_aangeleverd ?? 0}</li>
            <li>Aangemaakt: {result.aantal_aangemaakt ?? 0}</li>
            <li>Fouten: {result.aantal_fouten ?? result.errors?.length ?? 0}</li>
          </ul>
          {result.errors?.length ? (
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead><tr><th className="px-2 py-1 text-left">Rij</th><th className="px-2 py-1 text-left">Veld</th><th className="px-2 py-1 text-left">Fout</th></tr></thead>
                <tbody>
                  {result.errors.slice(0, 50).map((f, i) => (
                    <tr key={i}><td className="px-2 py-1">{f.rij_nummer ?? i + 1}</td><td className="px-2 py-1">{f.veld || "—"}</td><td className="px-2 py-1">{f.fout_melding || f.fout_code || JSON.stringify(f)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}


function RapportTab({ werkgeverId }) {
  const kpiQ = useQuery({
    queryKey: ["werkgever-kpi", werkgeverId],
    queryFn: () => adminGet(`/api/admin/werkgevers/${werkgeverId}/kpi`),
    enabled: !!werkgeverId,
  });
  const kpi = kpiQ.data || {};
  const rapportUrl = `/api/admin/werkgevers/${werkgeverId}/rapport`;

  return (
    <div className="space-y-4" data-testid="werkgever-rapport-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">KPI-overzicht</h2>
        <a
          href={rapportUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
        >
          Open rapport (print → PDF)
        </a>
      </div>
      {kpiQ.isLoading ? (
        <p className="text-sm text-slate-500">Laden…</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard label="Werknemers" value={kpi.aantal_werknemers ?? "—"} />
          <KpiCard label="Open signalen" value={`${kpi.signalen_totaal_open ?? "—"} / ${kpi.signalen_totaal ?? 0}`} />
          <KpiCard label="Gem. pijn baseline" value={kpi.gem_pijn_baseline ?? "—"} />
          <KpiCard label="Gem. pijn wk12" value={kpi.gem_pijn_wk12 ?? "—"} />
          <KpiCard label="Klinisch verbeterd" value={kpi.klinisch_verbeterd_pct != null ? `${kpi.klinisch_verbeterd_pct}%` : "—"} sub={kpi.klinisch_verbeterd_paar_count ? `n=${kpi.klinisch_verbeterd_paar_count}` : ""} />
        </div>
      )}
      {kpi.top_3_regels?.length > 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-xs font-semibold uppercase text-slate-500">Top-3 meest voorkomende regels</h3>
          <ul className="mt-2 space-y-1 text-sm">
            {kpi.top_3_regels.map((r) => (
              <li key={r.regel_id} className="flex justify-between">
                <code className="text-slate-900">{r.regel_id}</code>
                <span className="text-slate-600">{r.aantal}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 shadow-sm">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-0.5 text-xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}
